import { useCallback, useEffect, useMemo, useState } from 'react'
import { evidenceApi, type EvidenceHistoryRow, type EvidenceMetricSnapshot } from '../evidence/public'

const CONTROLLER_CONTRACT_VERSION = 'evidence-controller/v1'
const CONFIGURATION_CONTRACT_VERSION = 'config-resolution/v1'

type GovernanceSurfaceProps = Readonly<{
  tenantId: string
  canManageSystem: boolean
}>

function formatTimestamp(value: string | null): string {
  if (!value) return 'Chưa có dữ liệu'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Không xác định' : date.toLocaleString()
}

export function GovernanceSurface({ tenantId, canManageSystem }: GovernanceSurfaceProps) {
  const [metrics, setMetrics] = useState<EvidenceMetricSnapshot | null>(null)
  const [history, setHistory] = useState<EvidenceHistoryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!canManageSystem || !tenantId) return
    setLoading(true)
    setError('')
    const [metricResult, historyResult] = await Promise.all([
      evidenceApi.metrics(tenantId),
      evidenceApi.history({ tenantId, limit: 50 }),
    ])
    if (metricResult.error || historyResult.error) {
      setError(metricResult.error?.message ?? historyResult.error?.message ?? 'Không thể tải system evidence.')
      setMetrics(metricResult.data)
      setHistory(historyResult.data ?? [])
      setLoading(false)
      return
    }
    setMetrics(metricResult.data)
    setHistory(historyResult.data ?? [])
    setLoading(false)
  }, [canManageSystem, tenantId])

  useEffect(() => { void load() }, [load])

  const latest = history[0] ?? null
  const policyVersion = useMemo(() => history.find((event) => event.policyVersion !== null)?.policyVersion ?? null, [history])
  const exceptionCount = metrics?.errorCount ?? 0
  const health = !metrics ? 'UNKNOWN' : metrics.errorCount > 0 || metrics.completenessRatio < 1 ? 'ATTENTION' : 'HEALTHY'

  if (!canManageSystem) return null

  return <section id="system-governance" className="panel" aria-labelledby="system-governance-title">
    <div className="section-heading">
      <div><p className="eyebrow">MANAGER PACTLANE</p><h1 id="system-governance-title">System & Governance</h1></div>
      <button type="button" onClick={() => void load()} disabled={loading}>{loading ? 'Đang làm mới…' : 'Làm mới evidence'}</button>
    </div>
    <p className="muted">Surface này chỉ hiển thị operational metadata và audit-safe evidence. Không đọc campaign content, creator contact, financial data hoặc private evaluation.</p>
    {error && <div className="notice error" role="alert">{error}</div>}

    <div className="two-column">
      <article className="panel">
        <p className="eyebrow">DECISIONS / EXCEPTIONS</p>
        <h2>{exceptionCount > 0 ? `${exceptionCount} exception cần chú ý` : 'Không có exception evidence đang báo lỗi'}</h2>
        <p className="muted">Nguồn: Evidence Controller metrics. Không suy diễn business performance từ system evidence.</p>
        <a href="#audit-governance">Mở audit evidence</a>
      </article>
      <article className="panel">
        <p className="eyebrow">WORKSPACE / SYSTEM HEALTH</p>
        <h2>{health}</h2>
        <ul className="check-list">
          <li>Evidence completeness: {metrics ? `${Math.round(metrics.completenessRatio * 100)}%` : 'Chưa có dữ liệu'}</li>
          <li>Ingestion lag tối đa: {metrics ? `${metrics.maxIngestionLagMs} ms` : 'Chưa có dữ liệu'}</li>
          <li>Last sync/evidence: {formatTimestamp(latest?.ingestedAt ?? null)}</li>
          <li>Failure evidence: {metrics?.errorCount ?? 'Chưa có dữ liệu'}</li>
        </ul>
      </article>
    </div>

    <div className="two-column">
      <article className="panel">
        <p className="eyebrow">GOVERNANCE</p>
        <h2>Policy & controller contracts</h2>
        <ul className="check-list">
          <li>Policy version: {policyVersion ?? 'Chưa có policy version trong evidence'}</li>
          <li>Controller contract: {CONTROLLER_CONTRACT_VERSION}</li>
          <li>Latest event version: {latest?.eventVersion ?? 'Chưa có dữ liệu'}</li>
          <li>Audit provenance: {latest?.provenance ?? 'Chưa có dữ liệu'}</li>
        </ul>
      </article>
      <article className="panel">
        <p className="eyebrow">CONFIGURATION</p>
        <h2>Resolved configuration boundary</h2>
        <ul className="check-list">
          <li>Configuration contract: {CONFIGURATION_CONTRACT_VERSION}</li>
          <li>Tenant scope: current membership only</li>
          <li>Sensitive mutations: không expose trên surface này</li>
          <li>Privileged actions: phải đi qua permission + structured confirmation + audited application boundary</li>
        </ul>
      </article>
    </div>

    <article id="audit-governance" className="panel">
      <p className="eyebrow">JOBS / INTEGRATIONS / AUDIT</p>
      <h2>Evidence Controller status</h2>
      <p className="muted">Freshness, last sync và failure được lấy từ evidence API thay vì hard-code trạng thái provider.</p>
      <ul className="check-list">
        <li>Status: {health}</li>
        <li>Freshness: {formatTimestamp(latest?.ingestedAt ?? null)}</li>
        <li>Last observed event: {latest ? `${latest.eventKind} · ${latest.actionOrObservation}` : 'Chưa có dữ liệu'}</li>
        <li>Correlation: {latest?.correlationId ?? 'Chưa có dữ liệu'}</li>
      </ul>
    </article>
  </section>
}
